import type { CustomImpositionLayoutIssue, CustomImpositionRectangle } from "./types";
import { cmToUm, UM_PER_MM } from "./custom-imposition.coordinates";
import { resolveCustomValidatorInsetUm } from "./custom-imposition.margins";

export function shouldInvalidateForMaterial(
  layoutMaterialSizeId: string | null | undefined,
  nextMaterialSizeId: string | null | undefined,
): boolean {
  if (!layoutMaterialSizeId || !nextMaterialSizeId) {
    return false;
  }
  return layoutMaterialSizeId !== nextMaterialSizeId;
}

export function shouldInvalidateForUnit(
  previousWidthUm: number,
  previousHeightUm: number,
  nextWidthUm: number,
  nextHeightUm: number,
  tolerance = 10,
): boolean {
  if (!previousWidthUm || !previousHeightUm || !nextWidthUm || !nextHeightUm) {
    return false;
  }
  return (
    Math.abs(previousWidthUm - nextWidthUm) > tolerance ||
    Math.abs(previousHeightUm - nextHeightUm) > tolerance
  );
}

export function findLayoutIssues(
  positions: CustomImpositionRectangle[],
  sheetWidthUm: number,
  sheetLengthUm: number,
  options?: {
    marginXUm?: number;
    marginYUm?: number;
    bleedXUm?: number;
    bleedYUm?: number;
  },
): CustomImpositionLayoutIssue[] {
  const insetX = resolveCustomValidatorInsetUm(options?.marginXUm ?? 0);
  const insetY = resolveCustomValidatorInsetUm(options?.marginYUm ?? 0);
  const bleedX = Math.max(0, options?.bleedXUm ?? 0);
  const bleedY = Math.max(0, options?.bleedYUm ?? 0);
  const issues: CustomImpositionLayoutIssue[] = [];
  positions.forEach((position, index) => {
    if (position.width <= 0 || position.length <= 0) {
      issues.push({ type: "invalidSize", index });
    }
    const right = position.x + position.width;
    const bottom = position.y + position.length;
    if (
      position.x < insetX ||
      position.y < insetY ||
      right > sheetWidthUm - insetX ||
      bottom > sheetLengthUm - insetY ||
      position.x - bleedX < 0 ||
      position.y - bleedY < 0 ||
      right + bleedX > sheetWidthUm ||
      bottom + bleedY > sheetLengthUm
    ) {
      issues.push({ type: "outOfBounds", index });
    }
  });

  for (let i = 0; i < positions.length; i += 1) {
    for (let j = i + 1; j < positions.length; j += 1) {
      if (rectanglesOverlap(positions[i], positions[j])) {
        issues.push({ type: "overlap", index: i });
        issues.push({ type: "overlap", index: j });
      }
    }
  }
  return issues;
}

export function usedAreaPercent(
  positions: CustomImpositionRectangle[],
  sheetWidthUm: number,
  sheetLengthUm: number,
): number {
  const sheetArea = sheetWidthUm * sheetLengthUm;
  if (sheetArea <= 0) {
    return 0;
  }
  const used = positions.reduce(
    (sum, position) => sum + Math.max(0, position.width) * Math.max(0, position.length),
    0,
  );
  return Math.min(100, Math.round((used / sheetArea) * 100));
}

export function resolveSheetFromAttachment(output: {
  materialWidth?: number;
  materialLength?: number;
} | null | undefined): { widthUm: number; lengthUm: number } | null {
  if (!output?.materialWidth || !output?.materialLength) {
    return null;
  }
  return { widthUm: output.materialWidth, lengthUm: output.materialLength };
}

export function parseMaterialSizeLabel(label: string | undefined): {
  widthUm: number;
  lengthUm: number;
} | null {
  if (!label) {
    return null;
  }
  const match = label.replace(/,/g, ".").match(/(\d+(?:\.\d+)?)\s*[xX×/]\s*(\d+(?:\.\d+)?)/);
  if (!match) {
    return null;
  }
  const first = Number(match[1]);
  const second = Number(match[2]);
  if (!Number.isFinite(first) || !Number.isFinite(second) || first <= 0 || second <= 0) {
    return null;
  }
  const looksLikeMm = first >= 200 || second >= 200;
  return looksLikeMm
    ? { widthUm: Math.round(first * UM_PER_MM), lengthUm: Math.round(second * UM_PER_MM) }
    : { widthUm: cmToUm(first), lengthUm: cmToUm(second) };
}

export function findAttachmentOutput<T extends { name?: string; orginalName?: string }>(
  outputs: T[] | undefined,
): T | undefined {
  return outputs?.find((output) => {
    const name = (output.orginalName || output.name || "").replace(/\s/g, "").toLowerCase();
    return name === "attachment";
  });
}

export function readParameterDimensionUm(
  parameters: Array<{ parameterCode?: string; values?: any[] }> | undefined,
  code: string,
): number {
  const parameter = parameters?.find(
    (item) => item.parameterCode?.toLowerCase() === code.toLowerCase(),
  );
  const raw = Number(parameter?.values?.[0]);
  if (!Number.isFinite(raw) || raw <= 0) {
    return 0;
  }
  return cmToUm(raw);
}

function rectanglesOverlap(
  left: CustomImpositionRectangle,
  right: CustomImpositionRectangle,
): boolean {
  return (
    left.x < right.x + right.width &&
    right.x < left.x + left.width &&
    left.y < right.y + right.length &&
    right.y < left.y + left.length
  );
}
